import { useMemo } from 'react';

/** 接口可能返回 view_type 或 viewType */
const pickPageSelectorViewType = (input: {
  view_type?: string;
  viewType?: string;
}): string | undefined => {
  return input?.view_type || input?.viewType;
};

// 接口筛选器
export interface IPageSelector {
  key: string;
  default: string[];
  name_cn?: string;
  name_en?: string;
  visible: 0 | 1;
  /** 如 cascade / group，未返回时沿用前端 defaultSelector */
  view_type?: string;
  viewType?: string;
}

// 前端筛选器
export interface IDefaultSelector extends Omit<IPageSelector, 'default' | 'view_type' | 'visible'> {
  dataKey?: string;
  default: any;
  visible?: number;
  render?: (props: any) => any;
}

export interface IUseInputListProps {
  loading: boolean;
  // 后端配置筛选器
  pageSelector: IPageSelector[];
  // 前端兜底筛选器
  defaultSelector: IDefaultSelector[];
}

// 获取最终筛选器
export const useInputList = (props: IUseInputListProps) => {
  const { loading, pageSelector, defaultSelector } = props;
  // 设置inputList
  const inputList = useMemo(() => {
    if (loading) return [];
    // 后端未配置使用默认的筛选器（空数组与 undefined 同等对待）
    if (!pageSelector?.length) return defaultSelector;
    // 筛选器私有化的处理显示隐藏
    const list: IDefaultSelector[] = [];
    defaultSelector?.forEach((item: IDefaultSelector) => {
      // 匹配后端配置的筛选器
      const input = pageSelector?.find((v: IPageSelector) => v?.key === item?.dataKey);
      // 日期筛选器直接添加
      if (['singleDaily', 'contrastDaily', 'rangeTime'].includes(item?.key)) {
        list?.push(item);
      } else if (input && input?.visible !== 0) {
        // 判断需要展示的筛选器
        // 区域、产品、分类筛选器特殊处理
        if (input?.key === 'region') {
          list?.push({
            ...item,
            default: {
              markets: input?.default,
              region_type: 'market',
              regions: [],
            },
            name_cn: input?.name_cn || item?.name_cn,
            name_en: input?.name_en || item?.name_en,
            ...(pickPageSelectorViewType(input)
              ? { view_type: pickPageSelectorViewType(input) }
              : {}),
          });
        } else if (input?.key === 'category') {
          const productValue = pageSelector?.find(
            (v: IPageSelector) => v?.key === 'product' && v?.visible !== 0,
          );
          list?.push({
            ...item,
            default: {
              product: productValue?.default ?? ['255'],
              category_type: 'product',
              category: input?.default ?? ['255'],
            },
            name_cn: input?.name_cn || item?.name_cn,
            name_en: input?.name_en || item?.name_en,
            ...(pickPageSelectorViewType(input)
              ? { view_type: pickPageSelectorViewType(input) }
              : {}),
          });
        } else {
          list?.push({
            ...item,
            default: input?.default,
            visible: input?.visible,
            name_cn: input?.name_cn || item?.name_cn,
            name_en: input?.name_en || item?.name_en,
            ...(pickPageSelectorViewType(input)
              ? { view_type: pickPageSelectorViewType(input) }
              : {}),
          });
        }
      }
    });
    return list;
  }, [loading, pageSelector, defaultSelector]);

  return inputList;
};

// 获取未展示的筛选器默认值
export const useNoVisibleValue = (
  pageSelector: IPageSelector[],
) => {
  // 筛选器私有化的处理隐藏筛选器的值
  const noVisibleValue = useMemo(() => {
    const obj: any = {};
    pageSelector?.forEach((item: IPageSelector) => {
      if (item?.visible === 0) {
        // 区域、产品、分类筛选器特殊处理
        if (item?.key === 'region') {
          obj[item.key] = {
            markets: item?.default,
            region_type: 'market',
            regions: [],
          };
        } else if (item?.key === 'category') {
          const productValue = pageSelector?.find(
            (v: IPageSelector) => v?.key === 'product' && v?.visible === 0,
          );
          obj[item.key] = {
            product: productValue?.default ?? ['255'],
            category_type: 'product',
            category: item?.default ?? ['255'],
          };
        } else {
          obj[item?.key] = item?.default;
        }
      }
    });
    return obj;
  }, [pageSelector]);
  return noVisibleValue;
};
